/**
 * Raio de quina das caixas arredondadas.
 *
 * Uma caixa de canto vivo não pega reflexo na aresta, e é isso que denuncia o
 * "feito de caixas". O raio acompanha o tamanho da peça — uma maçaneta e um bloco
 * de concreto não têm a mesma quina —, mas com um teto, para o bloco não virar
 * almofada.
 */

/** Teto do raio, em metros. */
export const RAIO_MAXIMO = 0.02

/**
 * Abaixo disso a caixa fica reta. Decalques, lâminas de corte e chapas finas
 * continuam retos (uma lâmina arredondada seria errada, e um decalque de 2 mm
 * não tem onde ter quina), e não se pagam triângulos por uma quina de 1 mm que
 * ninguém vê.
 */
export const RAIO_MINIMO = 0.003

/** Parcela da menor dimensão que vira raio quando não se pede um valor. */
export const FRACAO_DA_MENOR_DIMENSAO = 0.22

/** O raio nunca passa disto da menor dimensão: com 0,5 as quinas opostas se encontrariam. */
const LIMITE_DA_MENOR_DIMENSAO = 0.45

export type Dimensoes = readonly [number, number, number]

/** Raio efetivo, em metros, ou 0 quando a caixa deve ficar reta. */
export function raioDaCaixa(dimensoes: Dimensoes, raioPedido?: number): number {
  const menor = Math.min(Math.abs(dimensoes[0]), Math.abs(dimensoes[1]), Math.abs(dimensoes[2]))
  const desejado = raioPedido ?? Math.min(RAIO_MAXIMO, menor * FRACAO_DA_MENOR_DIMENSAO)
  const raio = Math.min(desejado, menor * LIMITE_DA_MENOR_DIMENSAO)
  return raio < RAIO_MINIMO ? 0 : raio
}

/**
 * Segmentos por quina. Com um raio pequeno um único segmento já dá o reflexo
 * e custa 108 triângulos por caixa; dois custam 300 e só se notam em quinas
 * grandes. A cena da oficina já tem mais de 240 caixas visíveis: o orçamento é
 * de ~15% de triângulos a mais, então dois segmentos ficam para os blocos
 * grandes (quina de 1,5 cm ou mais, o que pede uma peça de ao menos ~7 cm).
 */
export function segmentosDoRaio(raio: number): number {
  return raio >= 0.015 ? 2 : 1
}
